import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, LinearProgress, Typography } from '@mui/material';

/**
 * Formatted SOA preview: the statement document the server builds (the same one Puppeteer prints) in a
 * frame that may run scripts but has no access to PRPO (sandbox without allow-same-origin: no login,
 * cookies or API). The document reports its pages, warnings and errors with postMessage.
 */

export interface SoaRenderResult {
  pages: Array<{ key: string; count: number }>;
  warnings: string[];
  errors: string[];
}

interface Props {
  html: string | null;
  loading?: boolean;
  error?: string | null;
  height?: number | string;
  title?: string;
  emptyText?: string;
  onResult?: (result: SoaRenderResult) => void;
}

const SoaPreviewFrame: React.FC<Props> = ({
  html, loading = false, error = null, height = '75vh', title = 'Statement preview',
  emptyText = 'Choose a business partner and press Preview.', onResult,
}) => {
  const frame = useRef<HTMLIFrameElement>(null);
  const [result, setResult] = useState<SoaRenderResult | null>(null);

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (!frame.current || e.source !== frame.current.contentWindow) return;
      if (e.data && e.data.type === 'soa-result') {
        setResult(e.data.result as SoaRenderResult);
        onResult?.(e.data.result as SoaRenderResult);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [onResult]);

  useEffect(() => { setResult(null); }, [html]);

  const pages = result ? result.pages.reduce((sum, p) => sum + p.count, 0) : 0;
  const notes = result ? [...result.errors, ...result.warnings] : [];

  return (
    <Box>
      {loading && <LinearProgress sx={{ mb: 1 }} aria-label="Preparing the preview" />}
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {notes.length > 0 && (
        <Alert severity={result && result.errors.length ? 'error' : 'warning'} sx={{ mb: 1 }}>
          {notes.slice(0, 6).map((n) => <div key={n}>{n}</div>)}
        </Alert>
      )}
      {html ? (
        <>
          <iframe
            ref={frame}
            title={title}
            sandbox="allow-scripts"
            srcDoc={html}
            style={{ width: '100%', height, border: '1px solid #d0d7de', borderRadius: 4, background: '#e9ecef', display: 'block' }}
          />
          {result && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {pages} page{pages === 1 ? '' : 's'} as printed. The PDF is laid out the same way on the server.
            </Typography>
          )}
        </>
      ) : (
        <Box
          sx={{
            height, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed #c4c9d0',
            borderRadius: 1, color: 'text.secondary', bgcolor: '#f6f8fa', p: 2, textAlign: 'center',
          }}
        >
          {loading ? 'Preparing the preview…' : emptyText}
        </Box>
      )}
    </Box>
  );
};

export default SoaPreviewFrame;
